import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { useLazyLoadQuery } from 'react-relay';
import { skillsProgressTableQuery, skillsResultsReport } from '../../graphQLOperations/Skills';
import { selectedReportsFilter } from '../../redux/reducers/filterReportsSlice';
import { useAppSelector } from '../../redux/hooks';
import { getSkillTypeName } from '../../businessLogicService/interpreterValuesProgressTable';
import { SkillResult } from '../../models/Skill';
import { ProgressTableClassFilterState } from './ProgressTableClass';
import { SkillsProgressTableQuery } from '../../graphQLOperations/__generated__/SkillsProgressTableQuery.graphql';
import { SkillsResultsReportQuery } from '../../graphQLOperations/__generated__/SkillsResultsReportQuery.graphql';
import { PageInfo } from '../../models/PageInfo';
import { PageControls } from '../../models/PageControls';

interface Props {
  filterState: ProgressTableClassFilterState;
  setFetchingData: Dispatch<SetStateAction<boolean>>;
  setTotalRecords: Dispatch<SetStateAction<number>>;
  fetchingData: boolean;
  firstLoad: boolean;
  pageControls: PageControls;
  setFirstLoad: Dispatch<SetStateAction<boolean>>;
  setValues: Dispatch<SetStateAction<ProgressTableSkillsValue[]>>;
  resetFilterState: () => void;
}

export interface ProgressTableSkillsValue {
  skillName: string;
  skillId: string;
  skillType: string;
  code: string;
  skillsResults: { key: SkillResult; value: number }[];
}

const QuerySkillsProgressReport = ({
  filterState,
  fetchingData,
  firstLoad,
  setFetchingData,
  setFirstLoad,
  setTotalRecords,
  setValues,
  pageControls,
}: Props) => {
  const regex = /^\d+$/;
  const isMovingNext = pageControls.page! - pageControls.prevPage === 1;
  const isMovingPrevious = pageControls.page! - pageControls.prevPage === -1;
  const isNotMoving = pageControls.page! - pageControls.prevPage === 0;
  const reportsFilterRedux = useAppSelector(selectedReportsFilter);
  const [pageInfo, setPageInfo] = useState<PageInfo | undefined>(undefined);
  const [temporaryValues, setTemporaryValues] = useState<ProgressTableSkillsValue[]>([]);

  const { allSkills } = useLazyLoadQuery<SkillsProgressTableQuery>(
    skillsProgressTableQuery,
    {
      first: fetchingData && (isMovingNext || isNotMoving || !pageInfo?.hasPreviousPage) ? pageControls.rows : null,
      last: fetchingData && isMovingPrevious && pageInfo?.hasPreviousPage ? pageControls.rows : null,
      after: fetchingData && isMovingNext && pageInfo?.hasNextPage ? pageInfo?.endCursor : undefined,
      before: fetchingData && isMovingPrevious && pageInfo?.hasPreviousPage ? pageInfo?.startCursor : undefined,
      filter: {
        targetGroupIds: filterState.filters.targetGroupIds,
        standardGradeIds: filterState.filters.skillGradeIds?.length ? filterState.filters.skillGradeIds : null,
        skillName: regex.test(filterState?.filters?.name) ? null : filterState?.filters?.name,
        skillCode: regex.test(filterState?.filters?.name) ? filterState?.filters?.name : null,
        skillType: filterState?.filters?.skillType !== null ? filterState?.filters?.skillType?.id : null,
      },
      targetGroupsIds: filterState.filters.targetGroupIds,
      skip: !firstLoad && !fetchingData,
    },
    { fetchKey: JSON.stringify({ filterState, ...pageControls }), fetchPolicy: 'network-only' },
  );
  const shouldFetchResults = temporaryValues.length > 0 && !fetchingData && !firstLoad;

  const { skillsProgressReport } = useLazyLoadQuery<SkillsResultsReportQuery>(
    skillsResultsReport,
    {
      filter: {
        skillIds: shouldFetchResults ? temporaryValues.map(val => val.skillId) : [],
        gradeLevelIds: reportsFilterRedux?.grades?.length
          ? reportsFilterRedux?.grades?.map(grade => grade?.id)
          : undefined,
        classIds: reportsFilterRedux?.classes?.map(cl => cl?.id),
      },
      skip: !shouldFetchResults,
    },
    { fetchPolicy: 'network-only', fetchKey: JSON.stringify(temporaryValues) },
  );

  useEffect(() => {
    if (allSkills?.nodes && fetchingData) {
      setTotalRecords(allSkills?.totalCount ?? 0);
      setPageInfo(allSkills?.pageInfo);
      setFirstLoad(false);
      setTemporaryValues(
        allSkills?.nodes?.map(
          val =>
            ({
              skillId: val?.id ?? '',
              skillName: val?.name ?? '',
              skillType: getSkillTypeName(
                val?.targetSkills?.map(targetSkill => targetSkill?.targetSkillType?.name ?? ''),
              ),
              code: val?.code ?? '',
              skillsResults: [],
            }) as ProgressTableSkillsValue,
        ),
      );
    }
  }, [allSkills, fetchingData]);

  useEffect(() => {
    setValues([]);
    if (skillsProgressReport) {
      const newValues = temporaryValues.map(val => {
        const skillResults = skillsProgressReport?.find(sk => sk?.skill?.id === val.skillId);
        return {
          ...val,
          skillsResults:
            skillResults?.skillsResults?.map(result => ({ key: result?.key ?? '', value: result?.value ?? 0 })) ?? [],
        };
      });

      setValues(newValues);
    }
    setFetchingData(false);
  }, [skillsProgressReport]);

  return <div style={{ display: 'none' }}></div>;
};

export default QuerySkillsProgressReport;
